/* =============================================
   ATHLYST Premium Interactions Engine
   Scroll-reveal, parallax, magnetic effects,
   smooth counters, text splitting, stagger
   ============================================= */

(function () {
  'use strict';

  /* ----- Scroll Reveal with IntersectionObserver ----- */
  const revealElements = () => {
    const els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = parseInt(el.dataset.revealDelay || '0', 10);
            setTimeout(() => {
              el.classList.add('is-revealed');
            }, delay);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.01, rootMargin: '0px 0px 0px 0px' }
    );

    els.forEach((el) => io.observe(el));
  };

  /* ----- Auto-reveal: tag every major section automatically ----- */
  const autoTagSections = () => {
    const selectors = [
      '.grid-banner-section .grid-banner-wrapper',
      '.collapsible-content .collapsible-content__wrapper',
      '.hotspot-block-section .row',
      '.support-block .row',
      '.marquee-section',
      '.image-with-banner .row',
      '.template-index .section-collapsible-content',
      '.template-index .grid-banner',
      '.template-product .shopify-section',
      '.template-collection .main-collection',
      '.template-page .shopify-section',
      '.product-recommendations',
      '.featured-collection',
      '.founder-note-section',
      '.feature-highlight-section',
      '.feature-stats-section',
      '.google-reviews-section',
    ];

    let delay = 0;
    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (!el.dataset.reveal) {
          el.dataset.reveal = 'fade-up';
          el.dataset.revealDelay = String(delay);
          delay += 60;
        }
      });
    });

    // Stagger product cards
    document.querySelectorAll('.card-wrapper').forEach((card, i) => {
      if (!card.dataset.reveal) {
        card.dataset.reveal = 'fade-up';
        card.dataset.revealDelay = String(i * 80);
      }
    });

    // Stagger accordion items
    document.querySelectorAll('.accordion').forEach((acc, i) => {
      if (!acc.dataset.reveal) {
        acc.dataset.reveal = 'fade-up';
        acc.dataset.revealDelay = String(i * 100);
      }
    });
  };

  /* ----- Parallax on Scroll (subtle, GPU-accelerated) ----- */
  const parallaxElements = () => {
    const els = document.querySelectorAll('[data-parallax]');
    if (!els.length) return;

    const onScroll = () => {
      const scrollY = window.pageYOffset;
      els.forEach((el) => {
        const speed = parseFloat(el.dataset.parallax || '0.08');
        const rect = el.getBoundingClientRect();
        const offset = (rect.top + scrollY - window.innerHeight / 2) * speed;
        el.style.transform = `translate3d(0, ${offset}px, 0)`;
      });
    };

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          onScroll();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  };

  /* ----- Smooth Number Counter (count up on reveal) ----- */
  const counterAnimation = () => {
    const counters = document.querySelectorAll('[data-counter]');
    if (!counters.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const target = parseInt(el.dataset.counter, 10);
            const duration = parseInt(el.dataset.counterDuration || '2000', 10);
            const suffix = el.dataset.counterSuffix || '';
            let start = 0;
            const startTime = performance.now();

            const update = (now) => {
              const elapsed = now - startTime;
              const progress = Math.min(elapsed / duration, 1);
              // Ease-out cubic
              const eased = 1 - Math.pow(1 - progress, 3);
              const current = Math.round(eased * target);
              el.textContent = current.toLocaleString() + suffix;
              if (progress < 1) requestAnimationFrame(update);
            };

            requestAnimationFrame(update);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.3 }
    );

    counters.forEach((c) => io.observe(c));
  };

  /* ----- Magnetic Hover Effect on CTA Buttons ----- */
  const magneticButtons = () => {
    if (window.innerWidth < 1024) return; // skip on mobile/tablet

    const buttons = document.querySelectorAll(
      '.button--primary, .banner-button, .product-form__submit, .slideshow__text .button'
    );

    buttons.forEach((btn) => {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        btn.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`;
      });

      btn.addEventListener('mouseleave', () => {
        btn.style.transform = 'translate(0, 0)';
        btn.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        setTimeout(() => { btn.style.transition = ''; }, 400);
      });
    });
  };

  /* ----- Text Split Letter Animation for hero headings ----- */
  const splitTextReveal = () => {
    const headings = document.querySelectorAll('[data-split-text]');
    headings.forEach((heading) => {
      const text = heading.textContent.trim();
      heading.innerHTML = '';
      heading.setAttribute('aria-label', text);

      const words = text.split(' ');
      words.forEach((word, wi) => {
        const wordSpan = document.createElement('span');
        wordSpan.style.display = 'inline-block';
        wordSpan.style.overflow = 'hidden';

        const letters = word.split('');
        letters.forEach((letter, li) => {
          const span = document.createElement('span');
          span.textContent = letter;
          span.className = 'split-letter';
          span.style.display = 'inline-block';
          span.style.opacity = '0';
          span.style.transform = 'translateY(100%)';
          span.style.transition = `all 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${(wi * 60 + li * 30)}ms`;
          wordSpan.appendChild(span);
        });

        heading.appendChild(wordSpan);
        if (wi < words.length - 1) {
          const space = document.createTextNode('\u00A0');
          heading.appendChild(space);
        }
      });

      // Trigger after small delay
      setTimeout(() => {
        heading.querySelectorAll('.split-letter').forEach((l) => {
          l.style.opacity = '1';
          l.style.transform = 'translateY(0)';
        });
      }, 200);
    });
  };

  /* ----- Smooth Image Reveal (clip-path wipe) ----- */
  const imageReveal = () => {
    const images = document.querySelectorAll('[data-image-reveal]');
    if (!images.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('image-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    images.forEach((img) => io.observe(img));
  };

  /* ----- Cursor Glow (desktop only, subtle brand accent) ----- */
  const cursorGlow = () => {
    if (window.innerWidth < 1024 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const glow = document.createElement('div');
    glow.className = 'cursor-glow';
    document.body.appendChild(glow);

    let mouseX = 0, mouseY = 0, glowX = 0, glowY = 0;

    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    });

    const animate = () => {
      glowX += (mouseX - glowX) * 0.12;
      glowY += (mouseY - glowY) * 0.12;
      glow.style.left = glowX + 'px';
      glow.style.top = glowY + 'px';
      requestAnimationFrame(animate);
    };
    animate();
  };

  /* ----- Smooth Scroll Progress Bar ----- */
  const scrollProgress = () => {
    const bar = document.createElement('div');
    bar.className = 'scroll-progress-bar';
    document.body.appendChild(bar);

    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      bar.style.width = progress + '%';
    }, { passive: true });
  };

  /* ----- Tilt Effect on Product Cards (desktop) ----- */
  const cardTilt = () => {
    if (window.innerWidth < 1024) return;

    document.querySelectorAll('.card-wrapper .card').forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        const tiltX = (y - 0.5) * 4;
        const tiltY = (x - 0.5) * -4;
        card.style.transform = `perspective(800px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale(1.01)`;
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = 'perspective(800px) rotateX(0) rotateY(0) scale(1)';
        card.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        setTimeout(() => { card.style.transition = ''; }, 500);
      });
    });
  };

  /* ----- Horizontal Scroll Parallax on Marquee ----- */
  const marqueeEnhance = () => {
    const marquees = document.querySelectorAll('.marquee-list');
    if (!marquees.length) return;

    window.addEventListener('scroll', () => {
      const scrollY = window.pageYOffset;
      marquees.forEach((m) => {
        const rect = m.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          const shift = scrollY * 0.05;
          m.style.transform = `translateX(${-shift}px)`;
        }
      });
    }, { passive: true });
  };

  /* ----- Initialize Everything ----- */
  const init = () => {
    autoTagSections();
    revealElements();
    parallaxElements();
    counterAnimation();
    magneticButtons();
    imageReveal();
    cursorGlow();
    scrollProgress();
    cardTilt();
    marqueeEnhance();

    // Split text for hero headings
    document.querySelectorAll('.slideshow .banner__heading').forEach((h) => {
      h.dataset.splitText = '';
    });
    splitTextReveal();
  };

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Small delay to let Shopify finish rendering dynamic content
    setTimeout(init, 100);
  }

  // Re-init on Shopify section re-render (theme editor)
  document.addEventListener('shopify:section:load', () => {
    setTimeout(init, 200);
  });
})();
